import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatApiError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { BarChart3, Crown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = {
  lv: ['', 'Janvāris', 'Februāris', 'Marts', 'Aprīlis', 'Maijs', 'Jūnijs', 'Jūlijs', 'Augusts', 'Septembris', 'Oktobris', 'Novembris', 'Decembris'],
  en: ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

function StatCard({ label, value, subtext, accent = false }) {
  return (
    <div className={`p-4 rounded-2xl border ${accent ? 'border-[#D97736]/30 bg-[#FFF8F0]' : 'border-[#E5E4E0] bg-white'}`}>
      <div className="text-xs text-[#5C5C5C] mb-1">{label}</div>
      <div className={`font-outfit text-2xl font-bold ${accent ? 'text-[#D97736]' : 'text-[#121212]'}`}>{value}</div>
      {subtext && <div className="text-xs text-[#8A8A8A] mt-1">{subtext}</div>}
    </div>
  );
}

function CommissionsTab({ language }) {
  const isLV = language === 'lv';
  const [stats, setStats] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, l] = await Promise.all([
          axios.get(`${API}/admin/commissions/stats`),
          axios.get(`${API}/admin/commissions?limit=50`),
        ]);
        setStats(s.data);
        setList(l.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-7 h-7 animate-spin text-[#D97736]" />
      </div>
    );
  }
  if (!stats) {
    return <p className="text-center text-[#5C5C5C] py-12 text-sm">{isLV ? 'Nav datu' : 'No data'}</p>;
  }

  const eur = (n) => `€${(n ?? 0).toFixed(2)}`;
  const maxMonthly = Math.max(...stats.monthly.map(m => m.commission), 1);
  const monthLabel = (m) => `${MONTH_NAMES[language][m.month] || m.month} ${m.year}`;

  return (
    <div className="space-y-6" data-testid="admin-commissions-tab">
      {/* Headline cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label={isLV ? 'KOPĀ NOPELNĪTS (visi laiki)' : 'TOTAL EARNED (all-time)'}
          value={eur(stats.all_time.commission)}
          subtext={`${stats.all_time.orders} ${isLV ? 'pasūtījumi' : 'orders'} · ${(stats.all_time.rate * 100).toFixed(0)}% likme`}
          accent
        />
        <StatCard
          label={isLV ? 'ŠODIEN' : 'TODAY'}
          value={eur(stats.today.commission)}
          subtext={`${stats.today.count} ${isLV ? 'pārdošanas' : 'sales'}`}
        />
        <StatCard
          label={isLV ? 'ŠOMĒNES' : 'THIS MONTH'}
          value={eur(stats.this_month.commission)}
          subtext={`${stats.this_month.count} ${isLV ? 'pārdošanas' : 'sales'}`}
        />
        <StatCard
          label={isLV ? 'IEPRIEKŠĒJAIS MĒNESIS' : 'LAST MONTH'}
          value={eur(stats.last_month.commission)}
          subtext={`${stats.last_month.count} ${isLV ? 'pārdošanas' : 'sales'}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly chart */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-semibold text-[#121212] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#D97736]" />
              {isLV ? 'Pēdējie 12 mēneši' : 'Last 12 months'}
            </h3>
          </div>
          {stats.monthly.length === 0 ? (
            <p className="text-sm text-[#5C5C5C] py-8 text-center">{isLV ? 'Vēl nav datu' : 'No data yet'}</p>
          ) : (
            <div className="space-y-2" data-testid="commission-monthly-chart">
              {[...stats.monthly].reverse().map(m => (
                <div key={m.label} className="flex items-center gap-3 text-sm">
                  <div className="w-24 text-[#5C5C5C] flex-shrink-0">{monthLabel(m)}</div>
                  <div className="flex-1 bg-[#FAFAFA] rounded-full h-7 relative overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#D97736] to-[#E89055] rounded-full transition-all"
                      style={{ width: `${(m.commission / maxMonthly) * 100}%`, minWidth: m.commission > 0 ? '4px' : '0' }}
                    />
                  </div>
                  <div className="w-20 text-right font-medium text-[#121212] flex-shrink-0">{eur(m.commission)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top sellers */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
          <h3 className="font-outfit font-semibold text-[#121212] flex items-center gap-2 mb-4">
            <Crown className="w-5 h-5 text-[#D97736]" />
            {isLV ? 'Top 10 pārdevēji' : 'Top 10 sellers'}
          </h3>
          {stats.top_sellers.length === 0 ? (
            <p className="text-sm text-[#5C5C5C] py-8 text-center">{isLV ? 'Vēl nav pārdevēju' : 'No sellers yet'}</p>
          ) : (
            <div className="divide-y divide-[#F0EFEB]" data-testid="commission-top-sellers">
              {stats.top_sellers.map((s, i) => (
                <div key={s.seller_id} className="flex items-center gap-3 py-2.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700'
                    : i === 1 ? 'bg-gray-200 text-gray-700'
                    : i === 2 ? 'bg-orange-100 text-orange-700'
                    : 'bg-[#FAFAFA] text-[#5C5C5C]'
                  }`}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#121212] truncate">{s.name || s.email}</div>
                    <div className="text-xs text-[#8A8A8A] truncate">{s.count} {isLV ? 'pārdošanas' : 'sales'} · {eur(s.gross)} {isLV ? 'apgrozījums' : 'gross'}</div>
                  </div>
                  <div className="text-sm font-semibold text-[#D97736] flex-shrink-0">{eur(s.commission)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Raw list */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-outfit font-semibold text-[#121212]">
            {isLV ? 'Pēdējās komisijas' : 'Latest commissions'} ({list.total})
          </h3>
          <CommissionExportControls language={language} />
        </div>
        {list.items.length === 0 ? (
          <p className="text-sm text-[#5C5C5C] py-8 text-center">{isLV ? 'Vēl nav ierakstu' : 'No entries yet'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="commission-table">
              <thead>
                <tr className="border-b border-[#E5E4E0] text-xs text-[#8A8A8A] uppercase">
                  <th className="text-left py-2 pr-3">{isLV ? 'Datums' : 'Date'}</th>
                  <th className="text-left py-2 pr-3">{isLV ? 'Pasūtījums' : 'Order'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Apgrozījums' : 'Gross'}</th>
                  <th className="text-right py-2 pr-3">{isLV ? 'Komisija' : 'Commission'}</th>
                  <th className="text-right py-2">{isLV ? 'Pārdevējam' : 'To seller'}</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map(c => (
                  <tr key={c.id} className="border-b border-[#F0EFEB] hover:bg-[#FAFAFA]">
                    <td className="py-2 pr-3 text-[#5C5C5C]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-[#5C5C5C]">{c.order_id?.slice(-8) || '-'}</td>
                    <td className="py-2 pr-3 text-right">{eur(c.total_amount)}</td>
                    <td className="py-2 pr-3 text-right font-medium text-[#D97736]">{eur(c.commission_amount)}</td>
                    <td className="py-2 text-right text-[#5C5C5C]">{eur(c.seller_net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CommissionExportControls({ language }) {
  const isLV = language === 'lv';
  const now = new Date();
  const currentYear = now.getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState('all');
  const [downloading, setDownloading] = useState(false);

  const years = [];
  for (let y = currentYear; y >= currentYear - 4; y--) years.push(y);

  const download = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (year !== 'all') params.year = year;
      if (year !== 'all' && month !== 'all') params.month = month;
      const res = await axios.get(`${API}/admin/commissions/export`, {
        params,
        responseType: 'blob',
      });
      // Pull the filename out of Content-Disposition for a nice save name.
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : 'kolv-commissions.csv';
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e, isLV ? 'Lejupielāde neizdevās' : 'Download failed'));
    } finally {
      setDownloading(false);
    }
  };

  const monthLabels = MONTH_NAMES[language] || MONTH_NAMES.lv;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="commission-export-controls">
      <select
        value={year}
        onChange={e => setYear(e.target.value)}
        className="text-sm px-3 py-1.5 rounded-lg border border-[#E5E4E0] bg-white"
        data-testid="commission-export-year"
      >
        <option value="all">{isLV ? 'Visi gadi' : 'All years'}</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select
        value={month}
        onChange={e => setMonth(e.target.value)}
        disabled={year === 'all'}
        className="text-sm px-3 py-1.5 rounded-lg border border-[#E5E4E0] bg-white disabled:opacity-50"
        data-testid="commission-export-month"
      >
        <option value="all">{isLV ? 'Visi mēneši' : 'All months'}</option>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
          <option key={m} value={m}>{monthLabels[m]}</option>
        ))}
      </select>
      <Button
        size="sm"
        onClick={download}
        disabled={downloading}
        className="bg-[#D97736] hover:bg-[#C06528] text-white"
        data-testid="commission-export-btn"
      >
        {downloading
          ? (isLV ? 'Lejupielādē...' : 'Downloading...')
          : (isLV ? 'Lejupielādēt CSV' : 'Download CSV')}
      </Button>
    </div>
  );
}

export default CommissionsTab;
